import {
  CreateStripeSubscriptionMutationVariables,
  MemberAttributes,
  TeamAttributes,
  TeamMemberRole,
} from '@graphql/generated';

export type DeferredTeamMember = { email: string; role: TeamMemberRole };

type DeferredTeamValues = {
  name?: string | null;
  location?: {
    address?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
  skillIds?: (string | null | undefined)[] | null;
};

/**
 * Builds the createStripeSubscription variables for the deferred team
 * creation flow: the team does not exist yet, so the backend creates it from
 * teamAttributes (including e-mail invitations) when the subscription starts.
 * Quantity covers the owner plus every pending invitation.
 */
export const buildDeferredTeamSubscriptionVariables = ({
  values,
  avatarBlobId,
  pendingMembers,
  planId,
  priceId,
}: {
  values: DeferredTeamValues;
  avatarBlobId?: string;
  pendingMembers: DeferredTeamMember[];
  planId: string;
  priceId: string;
}): CreateStripeSubscriptionMutationVariables => ({
  teamAttributes: {
    name: values.name ?? '',
    address: values.location?.address ?? undefined,
    latitude: values.location?.latitude ?? undefined,
    longitude: values.location?.longitude ?? undefined,
    skillIds: (values.skillIds ?? []).filter((id): id is string => id != null),
    avatarBlobId,
    teamMembers: pendingMembers,
  },
  stripeSubscriptionAttributes: {
    name: planId,
    quantity: pendingMembers.length + 1,
    priceId,
  },
});

/**
 * Builds the createTeam attributes for the checkout screen's deferred flow
 * (TA-10009): the team is created at subscription time — right before
 * updatePaymentMethod, which needs a teamId — combining userId-based members
 * added in checkout with deferred e-mail invitations.
 */
export const buildCheckoutTeamAttributes = ({
  values,
  fallbackName,
  members,
  deferredMembers,
  avatarBlobId,
}: {
  values: DeferredTeamValues;
  fallbackName?: string | null;
  members: MemberAttributes[];
  deferredMembers?: DeferredTeamMember[] | null;
  avatarBlobId?: string;
}): TeamAttributes => ({
  // Sentinel fallback kept from the legacy flow — TeamAttributes.name is
  // required, so an empty string would fail backend validation
  name: values.name?.trim() || fallbackName?.trim() || 'name - missing',
  longitude: values.location?.longitude,
  latitude: values.location?.latitude,
  address: values.location?.address,
  skillIds: (values.skillIds ?? []).filter((id): id is string => id != null),
  members,
  teamMembers: deferredMembers ?? [],
  avatarBlobId,
});
